import type { ConversationPlacement, CrowdexAssistant } from '@crowdex/agent-contracts/api';
import {
  PREVIEW_AUTOMATIONS,
  PREVIEW_PROJECT_CONVERSATION,
  PREVIEW_PROJECT_TASKS,
} from '@/features/assistants/assistant-preview-data';
import type { WorkspaceDirectorySnapshot } from './directory-client';

/**
 * The assistants of the redesign's boards (Crowdex Mobile, Launch, Infra & CI, and the home
 * one), the automations they run and where the conversations sit, for `/desktop-preview` and
 * component tests.
 */
const at = '2026-10-02T09:00:00.000Z';

export { PREVIEW_AUTOMATIONS };

function project(
  id: string,
  name: string,
  color: string,
  conversationId: string,
  extra: Partial<CrowdexAssistant> = {},
): CrowdexAssistant {
  return {
    id,
    workspaceId: 'ws-crowlingo',
    name,
    goal: '',
    color,
    repositoryIds: ['repo-crowdex'],
    memberAgentIds: ['developer', 'reviewer', 'designer'],
    wakes: [],
    instructions: 'Navigation and database changes go to Careful.',
    conversationId,
    archivedAt: null,
    createdAt: at,
    updatedAt: at,
    avatarId: null,
    isHome: false,
    workflowIds: [],
    automationIds: [],
    defaultAutomation: 'standard',
    ...extra,
  };
}

export const PREVIEW_PROJECTS: CrowdexAssistant[] = [
  project('proj-mobile', 'Crowdex Mobile', '#8b7cf6', '0b7e3c52-6a8d-4c6f-9a51-2f6d1e0a7b11', {
    goal: 'Mobile navigation and cloud extensions',
    // D67: its instructions (which say its agent's choices of automation).
    instructions:
      'Small PRs, lint first.\nFrench copy.\nNavigation and database changes go to Careful; a goal in several parts to Work as a team.',
  }),
  project('proj-launch', 'Launch', '#e8835a', '1c8f4d63-7b9e-4d70-8b62-3a7e2f1b8c22', {
    repositoryIds: ['repo-site'],
  }),
  project('proj-infra', 'Infra & CI', '#4fc1c9', '2d905e74-8caf-4e81-9c73-4b8f3a2c9d33', {
    instructions: 'Database and deployment changes go to Careful; a one-line fix to Quick.',
    automationIds: ['quick', 'careful'],
    defaultAutomation: 'careful',
  }),
  // The home assistant: no repositories, never archived; listed first wherever it shows.
  project('proj-home', 'Assistant', '#7fb4ff', '3ea16f85-9dc0-4f92-8d84-5c9a4b3dae44', {
    isHome: true,
    avatarId: 'marie',
    repositoryIds: [],
    memberAgentIds: [],
    instructions: '',
  }),
];

function place(
  projectId: string | null,
  parentConversationId: string | null = null,
): ConversationPlacement {
  return { workspaceId: null, projectId, parentConversationId, workflowRun: null };
}

/**
 * A task of a project (lot 6): a child of the project's conversation, running an automation
 * (D65 Tasks, D68 a project's conversation).
 */
function task(
  projectId: string,
  workflowId: string,
  state: NonNullable<ConversationPlacement['workflowRun']>['state'],
  step: string | null,
): ConversationPlacement {
  const owner = PREVIEW_PROJECTS.find((entry) => entry.id === projectId);
  return {
    workspaceId: null,
    projectId,
    parentConversationId: owner?.conversationId ?? null,
    workflowRun: {
      workflowId,
      automationName: PREVIEW_AUTOMATIONS.find((entry) => entry.id === workflowId)?.name ?? null,
      definition: `.crowdex/automations/${workflowId}.json`,
      runId: `run-${workflowId}`,
      step,
      round: null,
      state,
      costUsd: null,
    },
  };
}

/** The sidebar's conversation of one of Crowdex Mobile's tasks (`PREVIEW_PROJECT_TASKS`). */
function mobileTask(title: string): ConversationPlacement {
  const found = PREVIEW_PROJECT_TASKS.find((entry) => entry.title === title);
  return {
    ...place('proj-mobile', PREVIEW_PROJECT_CONVERSATION),
    workflowRun: found?.workflowRun ?? null,
  };
}

/** Where the preview's conversations (`PREVIEW_THREADS`) sit, as the boards show them. */
const PREVIEW_PLACEMENTS: Record<string, ConversationPlacement> = {
  // Crowdex Mobile's conversations: the one shown in the boards, filed in it, and the tasks of
  // its Tasks tab, children of the project's conversation each running its workflow (D49, D65).
  // The sidebar and the Tasks tab list the same children and count the same "2 running" (H7);
  // the plan's wave 2 has no conversation yet.
  'preview-active': place('proj-mobile'),
  'preview-team': mobileTask('Mobile navigation'),
  'preview-swipe': mobileTask('Navigation swipe gestures'),
  'preview-extensions': mobileTask('Extensions cloud mobile issues'),
  'preview-copy': mobileTask('Drawer copy in French'),
  'preview-landing': task('proj-launch', 'standard', 'running', 'Develop'),
  'preview-teaser': place('proj-launch'),
  'preview-ci': place('proj-infra'),
  'preview-ci-docker': place('proj-infra'),
  'preview-e2e': place('proj-infra'),
  // A schedule's run: its row has a clock, its earlier runs are its children.
  'preview-weekly': {
    ...place(null),
    workflowRun: {
      workflowId: 'weekly-dependencies',
      definition: 'weekly-dependencies.json',
      runId: 'run-3',
      step: null,
      round: null,
      state: 'succeeded',
      costUsd: 0.4,
    },
  },
  'preview-weekly-run-1': place(null, 'preview-weekly'),
  'preview-weekly-run-2': place(null, 'preview-weekly'),
  'preview-scroll': place(null),
  'preview-release': place(null),
  'preview-drawer-copy': place(null),
  'preview-hero': place(null),
};

export const PREVIEW_WORKSPACE_DIRECTORY: WorkspaceDirectorySnapshot = {
  projects: PREVIEW_PROJECTS,
  placements: PREVIEW_PLACEMENTS,
};
